import type { FC } from "react";

import { DetailsFieldCell } from "../../../../common-ui/details/DetailsFieldCell";
import {
  MAX_LENGTH_EMPTY_VEHICLE_WEIGHT,
  MAX_LENGTH_HEIGHT,
  MAX_LENGTH_ILU_CODE,
  MAX_LENGTH_LENGTH,
  MAX_LENGTH_VEHICLE_REG,
  MAX_LENGTH_WIDTH,
} from "../../../../common/fieldLengths";
import { transformYesNoOption } from "../../../../common/utils/optionTransformers";
import {
  formatILUCodeString,
  formatRegistrationNumberString,
} from "../../../../common/utils/stringUtil";
import type { CraneableStatus } from "../../../../gql/graphql";
import {
  type AllIntermodalVehicleTypesQueryRefProps,
  useAllIntermodalVehicleTypesAsOptions,
} from "../../../../services/intermodal/hooks/useAllIntermodalVehicleTypesAsOptions";
import { DimensionsFieldGrid } from "../../../manage/details/components/edit/BookingDetailsEditContent";
import { yesNoOptions } from "../../../manage/details/components/sections/common";
import {
  getCraneableStatusApprovalInfo,
  transformIntermodalVehicleType,
} from "../../utils/detailsUtils";
import { UpdateIntermodalDropdownField } from "../fields/UpdateIntermodalDropdownField";
import { UpdateIntermodalNumericTextInputField } from "../fields/UpdateIntermodalNumericTextInputField";
import { UpdateIntermodalTextInputField } from "../fields/UpdateIntermodalTextInputField";
import { UpdateIntermodalValidationWrapper } from "../UpdateIntermodalValidationWrapper";

interface UpdateIntermodalVehicleSectionProps extends AllIntermodalVehicleTypesQueryRefProps {
  craneableStatus: CraneableStatus;
}

export const UpdateIntermodalVehicleSection: FC<
  UpdateIntermodalVehicleSectionProps
> = ({ craneableStatus, vehicleTypesQueryRef }) => {
  const [vehicleTypes] =
    useAllIntermodalVehicleTypesAsOptions(vehicleTypesQueryRef);
  const craneableStatusApprovalInfo =
    getCraneableStatusApprovalInfo(craneableStatus);

  return (
    <>
      <UpdateIntermodalValidationWrapper
        renderField={(isValid) => (
          <DetailsFieldCell
            label={"Vehicle registration"}
            className={"t_intermodal_vehicle_reg_field"}
            renderField={(id) => (
              <UpdateIntermodalTextInputField
                id={id}
                trackerCategory={"IntermodalUpdate"}
                name={"vehicleReg"}
                isValid={isValid}
                maxLength={MAX_LENGTH_VEHICLE_REG}
                maskInput={formatRegistrationNumberString}
              />
            )}
          />
        )}
        name={"vehicleReg"}
      />
      <UpdateIntermodalValidationWrapper
        renderField={(isValid) => (
          <DetailsFieldCell
            label={"ILU code"}
            className={"t_intermodal_ilu_code_field"}
            renderField={(id) => (
              <UpdateIntermodalTextInputField
                id={id}
                trackerCategory={"IntermodalUpdate"}
                name={"iluCode"}
                isValid={isValid}
                maxLength={MAX_LENGTH_ILU_CODE}
                maskInput={formatILUCodeString}
              />
            )}
          />
        )}
        name={"iluCode"}
      />
      <DetailsFieldCell
        label={"Vehicle type"}
        renderField={(id) => (
          <UpdateIntermodalDropdownField
            id={id}
            trackerCategory={"IntermodalUpdate"}
            options={vehicleTypes}
            name={"vehicleType"}
            transformOption={transformIntermodalVehicleType}
          />
        )}
      />
      <DetailsFieldCell
        label={"Craneable"}
        renderField={(id) => (
          <UpdateIntermodalDropdownField
            id={id}
            trackerCategory={"IntermodalUpdate"}
            transformOption={transformYesNoOption}
            name={"craneable"}
            options={yesNoOptions}
          />
        )}
        flex={1}
        approved={craneableStatusApprovalInfo ?? undefined}
      />
      <UpdateIntermodalValidationWrapper
        renderField={(isValid) => (
          <DetailsFieldCell
            label={"Empty vehicle weight (kg)"}
            renderField={(id) => (
              <UpdateIntermodalNumericTextInputField
                id={id}
                maxLength={MAX_LENGTH_EMPTY_VEHICLE_WEIGHT}
                trackerCategory={"IntermodalUpdate"}
                isValid={isValid}
                name={"emptyWeight"}
                onlyIntegers
              />
            )}
          />
        )}
        name={"emptyWeight"}
      />

      <DimensionsFieldGrid>
        <UpdateIntermodalValidationWrapper
          renderField={(isValid) => (
            <DetailsFieldCell
              label={"Length (m)"}
              renderField={(id) => (
                <UpdateIntermodalNumericTextInputField
                  id={id}
                  maxLength={MAX_LENGTH_LENGTH}
                  trackerCategory={"IntermodalUpdate"}
                  isValid={isValid}
                  name={"length"}
                />
              )}
            />
          )}
          name={"length"}
        />

        <UpdateIntermodalValidationWrapper
          renderField={(isValid) => (
            <DetailsFieldCell
              label={"Width (m)"}
              renderField={(id) => (
                <UpdateIntermodalNumericTextInputField
                  id={id}
                  maxLength={MAX_LENGTH_WIDTH}
                  trackerCategory={"IntermodalUpdate"}
                  isValid={isValid}
                  name={"width"}
                />
              )}
            />
          )}
          name={"width"}
        />

        <UpdateIntermodalValidationWrapper
          renderField={(isValid) => (
            <DetailsFieldCell
              label={"Height (m)"}
              renderField={(id) => (
                <UpdateIntermodalNumericTextInputField
                  id={id}
                  maxLength={MAX_LENGTH_HEIGHT}
                  trackerCategory={"IntermodalUpdate"}
                  isValid={isValid}
                  name={"height"}
                />
              )}
            />
          )}
          name={"height"}
        />
      </DimensionsFieldGrid>
    </>
  );
};
